import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import { FeatureFlagRequired, FormLabel, FormLabelVariant } from '../../generic';
import PackageLocatorFilter from './PackageLocatorFilter';
import type { WithResetSelectedPackage } from '../PackageInventoryContainer';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import useFetchPackages from '../hooks/useFetchPackages';
import type { SelectOption } from '../../../types';
import usePackageManagers from '../hooks/usePackageManagers';
import type { Fetcher } from '../../../../shared/fetchers';
import PackageManagerFilter from './PackageManager';
import FilterCard from '../../generic/Filter/FilterCard';
import { DISABLE_PACKAGE_LABELS } from '../../../../shared/featureFlags/flagNames';
import { useOrganizationPackageLabels } from '../../../hooks/api/packageLabels';
import PackageLabelsFilter from './PackageLabelsFilter';

type Props = {
  areFetchersLoading: boolean;
  fetcherOptions: Array<SelectOption<Fetcher>>;
  selectedFetchers: Array<SelectOption<Fetcher>>;
  arePackageLabelsLoading: boolean;
  packageLabelOptions: string[];
  selectedPackageLabels: string[];
  selectedLocators: Array<SelectOption<string>>;
  setFilters: (...args: unknown[]) => void;
};

/**
 * PackageInventoryFiltersProject has filters related to filtering
 * packages by package manager (fetcher), labels, and locator.
 */
const PackageInventoryFiltersPackages: VoidFunctionComponent<Props> = ({
  areFetchersLoading,
  fetcherOptions,
  selectedFetchers,
  arePackageLabelsLoading,
  packageLabelOptions,
  selectedPackageLabels,
  selectedLocators,
  setFilters,
}) => (
  <FilterCard id="package-inventory-package-filters" title="Package">
    <FormLabel label="Package manager" variant={FormLabelVariant.Filter}>
      <PackageManagerFilter
        isLoading={areFetchersLoading}
        fetcherOptions={fetcherOptions}
        selectedFetchers={selectedFetchers}
        setFilters={setFilters}
      />
    </FormLabel>
    <FeatureFlagRequired flagName={DISABLE_PACKAGE_LABELS} isNotEnabled>
      <FormLabel label="Labels" variant={FormLabelVariant.Filter}>
        <PackageLabelsFilter
          isLoading={arePackageLabelsLoading}
          packageLabelOptions={packageLabelOptions}
          selectedPackageLabels={selectedPackageLabels}
          setFilters={setFilters}
        />
      </FormLabel>
    </FeatureFlagRequired>
    <FormLabel label="Locator" variant={FormLabelVariant.Filter}>
      <PackageLocatorFilter locators={selectedLocators} setFilters={setFilters} />
    </FormLabel>
  </FilterCard>
);

/**
 * Stateful container for the package filters as they are on the main /packages page.
 */
const PackageInventoryFiltersPackagesContainer: VoidFunctionComponent<{
  withResetSelectedPackage: WithResetSelectedPackage;
}> = ({
  withResetSelectedPackage,
}) => {
  const { fetchers: fetcherOptions, isLoading: areFetchersLoading } = usePackageManagers();
  const selectedFetchers = useSelector(PackageInventoryStore.selectors.getFiltersFetchersOptions);

  // Load org package labels with the useOrganizationPackageLabels hook.
  const { data: packageLabelsResponse, isLoading: arePackageLabelsLoading } = useOrganizationPackageLabels();
  const packageLabels = packageLabelsResponse?.packageLabels ?? [];
  const packageLabelOptions = packageLabels.map((packageLabel) => packageLabel.name);
  const selectedPackageLabels = useSelector(PackageInventoryStore.selectors.getFiltersPackageLabels);

  const selectedLocators = useSelector(PackageInventoryStore.selectors.getFiltersLocatorsOptions);

  const setFilters = useFetchPackages(PackageInventoryStore.actions.setFilters);

  return (
    <PackageInventoryFiltersPackages
      areFetchersLoading={areFetchersLoading}
      fetcherOptions={fetcherOptions}
      selectedFetchers={selectedFetchers}
      arePackageLabelsLoading={arePackageLabelsLoading}
      packageLabelOptions={packageLabelOptions}
      selectedPackageLabels={selectedPackageLabels}
      selectedLocators={selectedLocators}
      setFilters={withResetSelectedPackage(setFilters)}
    />
  );
};

export default PackageInventoryFiltersPackagesContainer;
